'use client';

import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Link } from '@/components/link/Link';
import { NavPanel } from './NavPanel';
import { NavTrigger } from './NavTrigger';
import { clamp, isPanelGroup, navKey, navTriggerId } from '../nav-data';
import type { DropdownNavigation } from '../hooks/useDropdownNavigation';
import type { NavGroup } from '../types';

type DesktopNavbarProps = {
  groups: NavGroup[];
  locale: string;
  dropdown: DropdownNavigation;
};

/** Max width of a panel, used to keep it inside the viewport. */
const PANEL_MAX_WIDTH = 700;
const NARROW_CONTAINER_WIDTH = 1064;
const WIDE_CONTAINER_WIDTH = 2000;
/** Horizontal offset per nav item, as a share of the container. */
const PANEL_OFFSET_PERCENT = 16.2;

export const DesktopNavbar = ({
  groups,
  locale,
  dropdown,
}: DesktopNavbarProps) => {
  const {
    openGroup,
    openColumnItem,
    isClosing,
    shouldAnimate,
    close,
    closeNow,
    toggleGroup,
    openGroupByKey,
    toggleColumnItem,
  } = dropdown;

  const navRef = useRef<HTMLElement>(null);
  const panelWrapperRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(WIDE_CONTAINER_WIDTH);

  // Every group carries its own key so two entries sharing a title cannot
  // open together or collide in the aria wiring.
  const entries = groups.map((group, index) => ({
    group,
    index,
    key: navKey(group.title, index),
  }));
  const panelEntries = entries.filter((entry) => isPanelGroup(entry.group));

  const activeIndex =
    entries.find((entry) => entry.key === openGroup)?.index ?? -1;
  // Once the menu closes there is no active group, so the wrapper keeps the
  // slot of the last one instead of falling back to 0 and jumping while it
  // is still fading out.
  const [lastActiveIndex, setLastActiveIndex] = useState(0);
  useEffect(() => {
    if (activeIndex >= 0) setLastActiveIndex(activeIndex);
  }, [activeIndex]);
  const positionIndex = activeIndex >= 0 ? activeIndex : lastActiveIndex;
  const maxLeftPercent =
    ((containerWidth - PANEL_MAX_WIDTH) / containerWidth) * 100;
  const leftPercent = clamp(
    positionIndex * PANEL_OFFSET_PERCENT,
    0,
    maxLeftPercent,
  );

  useEffect(() => {
    const updateContainerWidth = () =>
      setContainerWidth(
        window.innerWidth < 1280
          ? NARROW_CONTAINER_WIDTH
          : WIDE_CONTAINER_WIDTH,
      );

    updateContainerWidth();
    window.addEventListener('resize', updateContainerWidth);

    return () => window.removeEventListener('resize', updateContainerWidth);
  }, []);

  useEffect(() => {
    if (!openGroup || isClosing) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!navRef.current?.contains(event.target as Node)) close();
    };

    document.addEventListener('mousedown', handlePointerDown);

    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [openGroup, isClosing, close]);

  // Escape closes from anywhere and hands focus back to the trigger that
  // opened the panel, so keyboard users are not dropped at the top of the page.
  useEffect(() => {
    if (!openGroup) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      const trigger = document.getElementById(navTriggerId(openGroup));
      close();
      trigger?.focus();
    };

    document.addEventListener('keydown', handleEscape);

    return () => document.removeEventListener('keydown', handleEscape);
  }, [openGroup, close]);

  const panelKeys = panelEntries.map((entry) => entry.key);

  /** Wraps around, so the arrow keys cycle rather than dead-end. */
  const focusTriggerAt = (position: number) => {
    if (!panelKeys.length) return;

    const wrapped = (position + panelKeys.length) % panelKeys.length;
    document.getElementById(navTriggerId(panelKeys[wrapped]))?.focus();
  };

  const handleTriggerKeyDown =
    (groupKey: string) => (event: React.KeyboardEvent<HTMLButtonElement>) => {
      const index = panelKeys.indexOf(groupKey);

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        focusTriggerAt(index + 1);
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        focusTriggerAt(index - 1);
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        openGroupByKey(groupKey);

        // The panel is already in the DOM but only becomes focusable once it
        // is shown, so the move into it waits for that paint.
        requestAnimationFrame(() => {
          panelWrapperRef.current
            ?.querySelector<HTMLElement>(
              'a[href]:not([tabindex="-1"]), button:not([disabled])',
            )
            ?.focus();
        });
      }
    };

  return (
    <nav
      ref={navRef}
      className="smzh-navbar relative flex items-center lg:space-x-6 xl:space-x-7"
    >
      {entries.map(({ group, key }) => (
        <div key={key} className="group py-2">
          {isPanelGroup(group) ? (
            <NavTrigger
              title={group.title}
              groupKey={key}
              isOpen={openGroup === key}
              onToggle={() => toggleGroup(key)}
              onKeyDown={handleTriggerKeyDown(key)}
            />
          ) : (
            <Link
              href={group.slug || `/${locale}/`}
              className="select-none font-[450] !text-brand-700 hover:text-gray-700"
              onClick={closeNow}
            >
              {group.title}
            </Link>
          )}
        </div>
      ))}

      {/* One wrapper slides between groups; the panels inside it are all
          server-rendered and take turns being visible. */}
      <div
        ref={panelWrapperRef}
        className={clsx(
          'dropdown-navigation-wrapper absolute top-[calc(100%+0px)] z-10 !ml-0 flex min-h-[500px] w-auto max-w-[854px] transform rounded-[8px] bg-white shadow-md transition-all duration-300 ease-in-out',
          shouldAnimate
            ? 'pointer-events-auto opacity-100'
            : 'pointer-events-none opacity-0',
        )}
        style={{
          left: `${leftPercent}%`,
          // `shouldAnimate` is only true once a panel is already showing, so
          // `left` slides when moving between groups but snaps when the menu
          // opens from closed. The wrapper stays mounted, so without this it
          // would glide in from wherever it was last parked.
          transition: shouldAnimate
            ? 'left 0.5s ease, transform 0.3s ease, opacity 0.3s ease'
            : 'opacity 0.3s ease, transform 0.3s ease',
        }}
      >
        {panelEntries.map(({ group, key }) => (
          <NavPanel
            key={key}
            group={group}
            groupKey={key}
            isActive={openGroup === key}
            openColumnItem={openColumnItem}
            onToggleColumnItem={toggleColumnItem}
            onNavigate={closeNow}
          />
        ))}
      </div>
    </nav>
  );
};

export default DesktopNavbar;
